import noop from 'utils/noop';
import * as actionTypes from './actionTypes';
// import {
//   SearchGroupIndex,
//   SearchQueryIndex,
//   CriteriaPropertyIndex,
//   DynamicValue,
//   FieldOption,
// } from './types';

/*------------------------------------------------------------------------------------------------*/
/* Fetching Query Actions                                                                         */
/*------------------------------------------------------------------------------------------------*/
/**
 * fetchQuery
 * Action for requesting fetch to get the search query.
 * @param { Number } id: The id of the search query being fetched.
 * @returns { Object } action
 */
export const fetchQuery = (id) => ({
  type: actionTypes.FETCH_QUERY,
  isFetchingQuery: true,
  id,
});

/**
 * fetchQueryError
 * Action for an error while fetching the search query.
 * @param { Object } error: The error while fetching the search query.
 * @returns { Object } action
 */
export const fetchQueryError = (error) => ({
  type: actionTypes.FETCH_QUERY_ERROR,
  isFetchingQuery: false,
  error,
});

/**
 * fetchQueryResponse
 * Action for the response after fetching the search query to display for the query builder.
 * @param { Object } data: The data object for the query builder containing:
 *        {
 *          combinator: string, The main combinator value of the search query.
 *          hasDynamicFields: boolean, If the search query has any dynamic field.
 *          orderBy: {}, The orderBy object of the search query.
 *          sizeLimit: number, The size limit of the search query.
 *          searchQueries: [], The search query data array containing the full query.
 *        }
 * @returns { Object } action
 */
export const fetchQueryResponse = ({
  combinator,
  hasDynamicFields,
  orderBy,
  sizeLimit,
  searchQueries,
  usesSmartRecos,
} = {}) => ({
  type: actionTypes.FETCH_QUERY_RESPONSE,
  isFetchingQuery: false,
  combinator,
  hasDynamicFields,
  orderBy,
  searchQueries,
  sizeLimit,
  presentState: { combinator, searchQueries },
  usesSmartRecos,
});

/*------------------------------------------------------------------------------------------------*/
/* Fetching Query Properties Actions                                                              */
/*------------------------------------------------------------------------------------------------*/
/**
 * fetchQueryProperties
 * Action for requesting fetch to get properties for the query builder.
 * @returns { Object } action
 */
export const fetchQueryProperties = () => ({
  type: actionTypes.FETCH_QUERY_PROPERTIES,
  isFetchingProperties: true,
});

/**
 * fetchQueryPropertiesError
 * Action for an error while fetching properties for the query builder.
 * @param { Object } error: The error while fetching the properties.
 * @returns { Object } action
 */
export const fetchQueryPropertiesError = (error) => ({
  type: actionTypes.FETCH_QUERY_PROPERTIES_ERROR,
  isFetchingProperties: false,
  error,
});

/**
 * fetchQueryPropertiesResponse
 * Action for updating the available properties for the query builder.
 * @param { Array } properties: The list of available properties for the query builder.
 * @returns { Object } action
 */
export const fetchQueryPropertiesResponse = (properties) => ({
  type: actionTypes.FETCH_QUERY_PROPERTIES_RESPONSE,
  isFetchingProperties: false,
  properties,
});

/*------------------------------------------------------------------------------------------------*/
/* Fields Actions                                                                                 */
/*------------------------------------------------------------------------------------------------*/
/**
 * updateFieldsForProperty
 * Action for updating the fields for a specific property.
 * @param { String } property: The value of the property to set fields for.
 * @param { Array[FieldOption] } fields: The list of fields for the property.
 * @returns { Object } action
 */
export const updateFieldsForProperty = (property, fields) => ({
  type: actionTypes.UPDATE_FIELDS_FOR_PROPERTY,
  property,
  fields,
});

export const updateFieldsForPropertyError = (error) => ({
  type: actionTypes.UPDATE_FIELDS_FOR_PROPERTY_ERROR,
  error,
});

export const updateFieldsForPropertyResponse = (fields) => ({
  type: actionTypes.UPDATE_FIELDS_FOR_PROPERTY_RESPONSE,
  fields,
});

/*------------------------------------------------------------------------------------------------*/
/* Field Options Actions                                                                          */
/*------------------------------------------------------------------------------------------------*/
/**
 * updateOptionsForField
 * Action for updating the field options for a specific field.
 * @param { String } property: The value of the property the field is under.
 * @param { String } field: The field to set options for.
 * @param { Array } options: The options for the field.
 * @returns { Object } action
 */
export const updateOptionsForField = (property, field, options) => ({
  type: actionTypes.UPDATE_OPTIONS_FOR_FIELD,
  property,
  field,
  options,
});

/**
 * updateOptionsForFieldError
 * Error while updating the field options for a field.
 * @param { Object } error: The error while setting the options.
 * @returns { Object } action.
 */
export const updateOptionsForFieldError = (error) => ({
  type: actionTypes.UPDATE_OPTIONS_FOR_FIELD_ERROR,
  error,
});

/**
 * updateOptionsForFieldResponse
 * Action for the response updating the field options.
 * @param { Object } fieldOptions: The object of updated field options.
 * @returns { Object } action
 */
export const updateOptionsForFieldResponse = (fieldOptions) => ({
  type: actionTypes.UPDATE_OPTIONS_FOR_FIELD_RESPONSE,
  fieldOptions,
});

/*------------------------------------------------------------------------------------------------*/
/* Validate Query Actions                                                                         */
/*------------------------------------------------------------------------------------------------*/
/**
 * validateQuery
 * Validates the query.
 * @param { Function } validationCallback: Callback function to run if search query is valid.
 * @param { Array } validationCallbackProps: Additional props to pass to callback.
 * @returns { Object } action.
 */
export const validateQuery = (validationCallback = noop, ...validationCallbackProps) => ({
  type: actionTypes.VALIDATE_QUERY,
  validationCallback,
  validationCallbackProps,
});

/**
 * validateQueryResponse
 * Response from validating the query.
 * @param { Boolean } showValidations: If validation should be shown.
 * @param { Array } validationMessages: List of validation messages to show.
 * @param { Array } searchQueries: Formatted search queries.
 * @returns { Object } action.
 */
export const validateQueryResponse = (showValidations, validationMessages, searchQueries) => ({
  type: actionTypes.VALIDATE_QUERY_RESPONSE,
  showValidations,
  validationMessages,
  searchQueries,
});

/*------------------------------------------------------------------------------------------------*/
/* Update Query Actions                                                                           */
/*------------------------------------------------------------------------------------------------*/
/**
 * updateQuery
 * Updates the query.
 * @param { Object } states: The updated states and search queries.
 */
export const updateQuery = ({ combinator, hasDynamicFields, searchQueries }) => ({
  type: actionTypes.UPDATE_QUERY,
  combinator,
  hasDynamicFields,
  searchQueries,
  presentState: { combinator, searchQueries },
});

/**
 * updateQueryError
 * Error updating the query.
 * @param { Object } error: The query error.
 * @returns { Object } action.
 */
export const updateQueryError = (error) => ({
  type: actionTypes.UPDATE_QUERY_ERROR,
  error,
});

/**
 * updateQueryState
 * Updates main query and redo/udo states.
 * @param { Object } states: The updated states and search queries.
 * @returns { Object } action.
 */
export const updateQueryState = ({
  combinator,
  hasDynamicFields,
  redoQueue,
  searchQueries,
  undoStack,
  presentState,
}) => ({
  type: actionTypes.UPDATE_QUERY_FROM_STATE,
  combinator,
  hasDynamicFields,
  redoQueue,
  searchQueries,
  undoStack,
  presentState,
});

/*------------------------------------------------------------------------------------------------*/
/* Base Query Actions                                                                             */
/*------------------------------------------------------------------------------------------------*/
/**
 * undoQuery
 * Undo state change.
 * @returns { Object } action.
 */
export const undoQuery = () => ({ type: actionTypes.UNDO_QUERY });

/**
 * redoQuery
 * Redo state change.
 * @returns { Object } action.
 */
export const redoQuery = () => ({ type: actionTypes.REDO_QUERY });

/**
 * toggleEditMode
 * Toggles edit mode.
 * @returns { Object } action.
 */
export const toggleEditMode = () => ({
  type: actionTypes.TOGGLE_EDIT_MODE,
  isViewMode: false,
});

/**
 * toggleViewMode
 * Toggles view mode.
 * @returns { Object } action.
 */
export const toggleViewMode = () => ({
  type: actionTypes.TOGGLE_VIEW_MODE,
  isViewMode: true,
});

/**
 * changeCombinator
 * Updates the main combinator value.
 * @param { String } combinator: The updated combinator value.
 * @returns { Object } action.
 */
export const changeCombinator = (combinator) => ({
  type: actionTypes.CHANGE_COMBINATOR,
  combinator,
});

export const resetQueryBuilder = () => ({ type: actionTypes.RESET_QUERY_BUILDER });

/*------------------------------------------------------------------------------------------------*/
/* Initialize Query Actions                                                                       */
/*------------------------------------------------------------------------------------------------*/
/**
 * initializeQuery
 * Initialize query with default search group.
 * @param { Object } searchGroup: The search group to add.
 * @param { Object } queryBuilderProps: Additional props for the query builder.
 * @returns { Object } action.
 */
export const initializeQuery = (searchGroup, queryBuilderProps) => ({
  type: actionTypes.INITIALIZE_QUERY,
  searchGroup,
  queryBuilderProps,
});

/**
 * initializeQueryResponse
 * Updates main query and redo/udo states.
 * @param { Object } states: The updated states and search queries.
 * @returns { Object } action.
 */
export const initializeQueryResponse = ({ searchQueries, combinator, hasDynamicFields }) => ({
  type: actionTypes.INITIALIZE_QUERY_RESPONSE,
  combinator,
  hasDynamicFields,
  redoQueue: [],
  searchQueries,
  undoStack: [],
  presentState: { combinator, searchQueries },
});

/*------------------------------------------------------------------------------------------------*/
/* Limit And Order By Actions                                                                     */
/*------------------------------------------------------------------------------------------------*/
/**
 * changeQueryOrderBy
 * Change order by values.
 * @param { Object } orderBy: The new orderBy value.
 * @returns { Object } action.
 */
export const changeQueryOrderBy = (orderBy) => ({
  type: actionTypes.CHANGE_QUERY_ORDER_BY,
  orderBy,
});

/**
 * changeQuerySizeLimit
 * Change size limit value.
 * @param { Number } sizeLimit: The new sizeLimit value.
 * @returns { Object } action.
 */
export const changeQuerySizeLimit = (sizeLimit) => ({
  type: actionTypes.CHANGE_QUERY_SIZE_LIMIT,
  sizeLimit,
});

/*------------------------------------------------------------------------------------------------*/
/* Recommendations Actions                                                                        */
/*------------------------------------------------------------------------------------------------*/
/**
 * setUsesSmartRecos
 * Toggle usesSmartRecos field.
 * collectionHasChanged controls the Save button for the collection preview query. It will be set to
 * True only if it's using smart recos, else if it's using the orderBy, it will reset to False since
 * there won't be a field selected to save.
 * @param { Boolean } usesSmartRecos: The new usesSmartRecos value.
 * @returns { Object } action.
 */
export const setUsesSmartRecos = (usesSmartRecos) => ({
  type: actionTypes.SET_USES_SMART_RECOS,
  collectionHasChanged: usesSmartRecos,
  usesSmartRecos,
});

/*------------------------------------------------------------------------------------------------*/
/* Search Group Actions                                                                           */
/*------------------------------------------------------------------------------------------------*/
/**
 * addSearchGroup
 * Add a search group.
 * @param { Object } searchGroup: The search group to add.
 * @returns { Object } action.
 */
export const addSearchGroup = (searchGroup) => ({
  type: actionTypes.ADD_SEARCH_GROUP,
  searchGroup,
});

/**
 * copySearchGroup
 * Copy a search group.
 * @param { Number } searchGroupIndex: The index of the search group to copy.
 * @returns { Object } action.
 */
export const copySearchGroup = (searchGroupIndex /*: SearchGroupIndex */) => ({
  type: actionTypes.COPY_SEARCH_GROUP,
  searchGroupIndex,
});

/**
 * deleteSearchGroup
 * Delete a search group.
 * @param { Number } searchGroupIndex: The index of the search group to delete.
 * @returns { Object } action.
 */
export const deleteSearchGroup = (searchGroupIndex /*: SearchGroupIndex */) => ({
  type: actionTypes.DELETE_SEARCH_GROUP,
  searchGroupIndex,
});

/**
 * updateSearchGroup
 * Updates a search group.
 * @param { Object } searchGroup: The updated search group.
 * @param { Number } searchGroupIndex: The index of the search group to updated.
 * @returns { Object } action.
 */
export const updateSearchGroup = (searchGroup, searchGroupIndex /*: SearchGroupIndex */) => ({
  type: actionTypes.UPDATE_SEARCH_GROUP,
  searchGroup,
  searchGroupIndex,
});

/**
 * changeSearchGroupCombinator
 * Change the combinator in a search group.
 * @param { String } combinator: The new combinator value.
 * @param { Number } searchGroupIndex: The index of the search group to change combinator.
 * @returns { Object } action.
 */
export const changeSearchGroupCombinator = (
  combinator,
  searchGroupIndex /*: SearchGroupIndex */,
) => ({
  type: actionTypes.CHANGE_SEARCH_GROUP_COMBINATOR,
  combinator,
  searchGroupIndex,
});

/*------------------------------------------------------------------------------------------------*/
/* Search Query Actions                                                                           */
/*------------------------------------------------------------------------------------------------*/
/**
 * addSearchQuery
 * Add a search query.
 * @param { Object } searchQuery: The searchQuery to add.
 * @param { Number } searchGroupIndex: The index of the search group to add searchQuery.
 * @returns { Object } action.
 */
export const addSearchQuery = (searchQuery, searchGroupIndex /*: SearchGroupIndex */) => ({
  type: actionTypes.ADD_SEARCH_QUERY,
  searchQuery,
  searchGroupIndex,
});

/**
 * copySearchQuery
 * Copies a search query.
 * @param { Number } searchQueryIndex: The index of the search query to copy.
 * @param { Number } searchGroupIndex: The index of the search group the search query is in.
 * @returns { Object } action.
 */
export const copySearchQuery = (
  searchQueryIndex /*: SearchQueryIndex */,
  searchGroupIndex /*: SearchGroupIndex */,
) => ({
  type: actionTypes.COPY_SEARCH_QUERY,
  searchQueryIndex,
  searchGroupIndex,
});

/**
 * deleteSearchQuery
 * Deletes a search query.
 * @param { Number } searchQueryIndex: The index of the search query to delete.
 * @param { Number } searchGroupIndex: The index of the search group the search query is in.
 * @returns { Object } action.
 */
export const deleteSearchQuery = (
  searchQueryIndex /*: SearchQueryIndex */,
  searchGroupIndex /*: SearchGroupIndex */,
) => ({
  type: actionTypes.DELETE_SEARCH_QUERY,
  searchQueryIndex,
  searchGroupIndex,
});

/**
 * updateSearchQuery
 * Updates a search query.
 * @param { Object } searchQuery: The updated search query.
 * @param { Number } searchGroupIndex: The index of the search group the search query is in.
 * @param { Number } searchQueryIndex: The index of the search query to update.
 * @returns { Object } action.
 */
export const updateSearchQuery = (
  searchQuery,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
) => ({
  type: actionTypes.UPDATE_SEARCH_QUERY,
  searchQuery,
  searchGroupIndex,
  searchQueryIndex,
});

/**
 * moveSearchQuery
 * Moves a search query.
 * @param { Number } searchQueryIndex: The index of the search query to move.
 * @param { String } searchGroupId: The ID of the group to move the search query from.
 * @param { Number } newSearchQueryIndex: The new index to move the search query to.
 * @param { String } newSearchGroupId: The ID of the search group to move the search query to.
 * @returns { Object } action.
 */
export const moveSearchQuery = (
  searchQueryIndex /*: SearchQueryIndex */,
  searchGroupId,
  newSearchQueryIndex /*: SearchQueryIndex */,
  newSearchGroupId,
) => ({
  type: actionTypes.MOVE_SEARCH_QUERY,
  searchQueryIndex,
  searchGroupId,
  newSearchQueryIndex,
  newSearchGroupId,
});

/**
 * reorderSearchQuery
 * Reorders a search query.
 * @param { Number } searchQueryIndex: The index of the search query to reorder.
 * @param { Number } newSearchQueryIndex: The new index of the search query.
 * @param { String } searchGroupId: The ID of the search group to reorder the search query from.
 * @returns { Object } action.
 */
export const reorderSearchQuery = (
  searchQueryIndex /*: SearchQueryIndex */,
  newSearchQueryIndex /*: SearchQueryIndex */,
  searchGroupId,
) => ({
  type: actionTypes.REORDER_SEARCH_QUERY,
  searchQueryIndex,
  newSearchQueryIndex,
  searchGroupId,
});

/**
 * changeProperty
 * Change the property (dataType) of a search query.
 * @param { String } property: The updated property value.
 * @param { Number } searchGroupIndex: The index of the search group the search query is in.
 * @param { Number } searchQueryIndex: The index of the search query to change property.
 * @returns { Object } action.
 */
export const changeProperty = (
  property,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
) => ({
  type: actionTypes.CHANGE_PROPERTY,
  property,
  searchGroupIndex,
  searchQueryIndex,
});

/**
 * changePropertyRefinement
 * Change the property (dataType) of a search query.
 * @param { Number } minMatch: The min property value.
 * @param { Number } maxMatch: The max property value.
 * @param { Number } searchGroupIndex: The index of the search group the search query is in.
 * @param { Number } searchQueryIndex: The index of the search query to change property.
 * @returns { Object } action.
 */
export const changePropertyRefinement = (
  minMatch,
  maxMatch,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
) => ({
  type: actionTypes.CHANGE_PROPERTY_REFINEMENT,
  minMatch,
  maxMatch,
  searchGroupIndex,
  searchQueryIndex,
});

/*------------------------------------------------------------------------------------------------*/
/* Criteria Property Actions                                                                      */
/*------------------------------------------------------------------------------------------------*/
/**
 * addCriteriaProperty
 * Add a criteria property.
 * @param { Object } criteriaProperty: The criteria property to add.
 * @param { Number } searchGroupIndex: The search group index to add criteria property to.
 * @param { Number } searchQueryIndex: The search query index to add the criteia property to.
 * @param { Number } criteriaPropertyIndex: The index to add the criteria property.
 * @returns { Object } action.
 */
export const addCriteriaProperty = (
  criteriaProperty,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.ADD_CRITERIA_PROPERTY,
  criteriaProperty,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * copyCriteriaProperty
 * Copy a criteria property.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to copy.
 * @param { Number } searchGroupIndex: The search group index to copy criteria property from.
 * @param { Number } searchQueryIndex: The search query index to copy the criteia property from.
 * @param { Boolean } isDynamic: If the field is a dynamic field.
 * @returns { Object } action.
 */
export const copyCriteriaProperty = (
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  isDynamic /*: boolean */,
) => ({
  type: actionTypes.COPY_CRITERIA_PROPERTY,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
  isDynamic,
});

/**
 * deleteCriteriaProperty
 * Delete a criteria property.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to delete.
 * @param { Number } searchGroupIndex: The search group index to delete criteria property from.
 * @param { Number } searchQueryIndex: The search query index to delete the criteia property from.
 * @returns { Object } action.
 */
export const deleteCriteriaProperty = (
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
) => ({
  type: actionTypes.DELETE_CRITERIA_PROPERTY,
  criteriaPropertyIndex,
  searchGroupIndex,
  searchQueryIndex,
});

/**
 * updateCriteriaProperties
 * Updates a criteria property in the given list of criteria properties.
 * @param { Number } criteriaProperty: The updated criteria property object.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to update.
 * @returns { Object } action.
 */
export const updateCriteriaProperties = (
  criteriaProperty,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.UPDATE_CRITERIA_PROPERTIES,
  criteriaProperty,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * changeCastType
 * Change the cast type of a criteria property.
 * @param { string } castType: The updated cast type.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change value.
 * @returns action.
 */
export const changeCastType = (
  castType,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.CHANGE_CAST_TYPE,
  castType,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * changeComparator
 * Change the comparator in a criteria property.
 * @param { String } comparator: The updated comparator value.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change comparator.
 * @param { Boolean } isDynamic: If the criteria property is dynamic.
 * @returns { Object } action.
 */
export const changeComparator = (
  comparator,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
  isDynamic,
) => ({
  type: actionTypes.CHANGE_COMPARATOR,
  comparator,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
  isDynamic,
});

/**
 * changeDynamicValue
 * Change the dynamic value of a criteria property.
 * @param { String } dynamicValue: The updated dynamic value.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change value.
 * @returns { Object } action.
 */
export const changeDynamicValue = (
  dynamicValue /*: DynamicValue */,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.CHANGE_DYNAMIC_VALUE,
  dynamicValue,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * changeField
 * Change the field of fieldType in a criteria property.
 * @param { String } field: The updated field value.
 * @param { String } fieldType: The updated field type value.
 * @param { String } valueType: The type of value the result is.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change field.
 * @returns { Object } action.
 */
export const changeField = (
  field,
  fieldType,
  valueType,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.CHANGE_FIELD,
  field,
  fieldType,
  valueType,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * changeGeoValue
 * Change the geo value of a criteria property.
 * @param { Number } lat: The latitude value.
 * @param { Number } lon: The longitude value.
 * @param { Number } distance: The distance value.
 * @param { String } unit: The distance unit.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change value.
 * @returns { Object } action.
 */
export const changeGeoValue = (
  lat,
  lon,
  distance,
  unit,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.CHANGE_GEO_VALUE,
  lat,
  lon,
  distance,
  unit,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * changeValue
 * Change the value of a criteria property.
 * @param { String | String[] } value: The updated value.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change value.
 * @returns { Object } action.
 */
export const changeValue = (
  value,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
  propertyName,
) => ({
  type: actionTypes.CHANGE_VALUE,
  value,
  propertyName,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * clearValue
 * Clear the value of a criteria property.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to clear value.
 * @returns { Object } action.
 */
export const clearValue = (
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
  propertyName,
) => ({
  type: actionTypes.CLEAR_VALUE,
  propertyName,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/**
 * toggleDynamic
 * Toggle if field is dynamic or not.
 * @param { String } valueType: The type of value the result is.
 * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
 * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
 * @param { Number } criteriaPropertyIndex: The index of the criteria property to change field.
 * @returns { Object } action.
 */
export const toggleDynamic = (
  valueType,
  searchGroupIndex /*: SearchGroupIndex */,
  searchQueryIndex /*: SearchQueryIndex */,
  criteriaPropertyIndex /*: CriteriaPropertyIndex */,
) => ({
  type: actionTypes.TOGGLE_DYNAMIC,
  valueType,
  searchGroupIndex,
  searchQueryIndex,
  criteriaPropertyIndex,
});

/*------------------------------------------------------------------------------------------------*/
/* Cloning Actions                                                                                 */
/*------------------------------------------------------------------------------------------------*/

export const setCloningValidationMessages = (validations) => ({
  type: actionTypes.SET_CLONING_VALIDATION_MESSAGES,
  validations,
});

/*------------------------------------------------------------------------------------------------*/
/* Catalog Actions                                                                                 */
/*------------------------------------------------------------------------------------------------*/

export const changeCatalogName = (catalogName /*: string */) => ({
  type: actionTypes.CHANGE_CATALOG_NAME,
  catalogName,
});
